// Shared holdings / depreciation math used by Modules and the alerts engine.
export const num = (v) => parseFloat(v) || 0;
export const money = (n) => `$${num(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const DEP_PERIODS = ["Daily", "Weekly", "Monthly", "Quarterly", "Every four months", "Annually"];
export const DEP_METHODS = ["Straight-line", "Declining balance"];
export const MAX_SCHEDULE_ROWS = 120;

export const depAmount = (r) => {
  const g = num(r.gross_value);
  if (r.dep_type === "Percentage") return g * (num(r.dep_value) / 100);
  if (r.dep_type === "Flat amount") return num(r.dep_value);
  return 0;
};

export const isDeclining = (r) => r.dep_method === "Declining balance" && r.dep_type === "Percentage";

export const periodsElapsed = (r) => {
  if (!r.acq_date || !r.dep_type || r.dep_type === "None") return 0;
  const start = new Date(r.acq_date); const now = new Date();
  if (isNaN(start.getTime())) return 0;
  const days = Math.max(0, (now - start) / 86400000);
  const months = Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) - (now.getDate() < start.getDate() ? 1 : 0));
  switch (r.dep_period) {
    case "Daily": return Math.floor(days);
    case "Weekly": return Math.floor(days / 7);
    case "Monthly": return months;
    case "Quarterly": return Math.floor(months / 3);
    case "Every four months": return Math.floor(months / 4);
    case "Annually": return Math.floor(months / 12);
    default: return 0;
  }
};

export const accumulatedDep = (r) => {
  if (!r.dep_type || r.dep_type === "None") return 0;
  const g = num(r.gross_value), salvage = num(r.salvage), n = periodsElapsed(r);
  if (isDeclining(r)) {
    const book = Math.max(salvage, g * Math.pow(1 - num(r.dep_value) / 100, n));
    return g - book;
  }
  return Math.min(depAmount(r) * n, Math.max(0, g - salvage));
};

export const bookValue = (r) => Math.max(num(r.salvage), num(r.gross_value) - accumulatedDep(r));

// Best-effort per-record value across seeder/holding formats (no network calls).
export const holdingValue = (h) => {
  if (!h) return 0;
  if (h.kind === "nft") return (num(h.nft_units) || 1) * num(h.nft_value);
  if (num(h.fmv)) return num(h.fmv);
  if (num(h.gross_value)) return num(h.gross_value);
  if (num(h.last_appraised)) return num(h.last_appraised);
  if (num(h.principal)) return num(h.principal);
  return 0;
};

export const buildSchedule = (r) => {
  const g = num(r.gross_value), salvage = num(r.salvage);
  const elapsed = periodsElapsed(r);
  const total = Math.max(elapsed, 1);
  const cap = Math.min(total, MAX_SCHEDULE_ROWS);
  const rows = [];
  let opening = g;
  for (let i = 1; i <= cap; i++) {
    if (opening <= salvage) break;
    let dep = isDeclining(r) ? opening * (num(r.dep_value) / 100) : depAmount(r);
    let closing = opening - dep;
    if (closing < salvage) { closing = salvage; dep = opening - salvage; }
    rows.push({ period: i, opening, dep, closing, elapsed: i <= elapsed });
    opening = closing;
  }
  return { rows, total, capped: total > MAX_SCHEDULE_ROWS, elapsed };
};
